/* General puprpose helper for tabs */

/*
    paramaters for constructor:
    tabContainer: string - the selector for container for the tabs
    tabs: string - the selector for the tabs, all tabs should have the same selector
    activeClass: string - the class to be added to the active tab
    targetContainer: string - the selector for the container for the targets
    targets: string - the selector for the targets, all targets should have the same selector
    targetActiveClass: string - the class to be added to the target element when it's match is active
    callback(tab, target) - a callback function that is called when a tab is clicked
*/

import {
    tap,
    pipe,
    focus,
    queryAll,
    addClass,
    removeClass,
    getKeyEvent,
    setTabindex,
    setTabIndexMinusOne
  } from '../assets/utils.js'
  
  // Constants
  const directions = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -1, ArrowDown: 1 }
  
  // Functions
  const queryLoop = object => Object.values(object).filter(Boolean).map((value) => queryAll(value))
  
  const getNextIndex = (currentIndex, arrayLength, direction) => {
    const nextIndex = currentIndex + direction
    if (nextIndex < 0) return arrayLength - 1
  
    if (nextIndex >= arrayLength) return currentIndex
  
    return nextIndex
  }
  
  const moveAcrossArray = pipe(
    tap(setTabindex),
    tap(focus)
  )
  
  const enableKeyBoardNavigation = (e, el, index, arr) => {
    const key = getKeyEvent(e)
    if (key in directions) {
      const nextIndex = getNextIndex(index, arr.length, directions[key])
      moveAcrossArray(arr[nextIndex])
      setTabIndexMinusOne(el)
    }
  }
  
  const activateMathcingTabAndContent = (index, tabElements, targetElements, tabActiveClass, targetActiveClass, callback, funcSettings) => {
    tabElements.forEach((tabElement) => removeClass(tabElement, tabActiveClass))
    addClass(tabElements[index], tabActiveClass)
    if (funcSettings?.beforeTargetClassAdded && funcSettings?.beforeTargetClassAdded(targetElements[index], tabElements[index])) return false
    targetElements.forEach((targetElement) => removeClass(targetElement, targetActiveClass))
    addClass(targetElements[index], targetActiveClass)
    callback && callback(tabElements[index], targetElements[index])
  }
  
  // main Class
  class Tabs {
    constructor({ tabContainer, tabs, activeClass, targetContainer, targets, targetActiveClass, callback, funcSettings }) {
      this.selectors = {
        targetContainer,
        tabContainer,
        targets,
        tabs
      }
      this.elements = queryLoop(this.selectors)
      this.classes = { activeClass, targetActiveClass }
      this.elementErrorHandle()
      this.classesErrorHandle()
      const [targetContainerElement, tabContainerElement, targetElements, tabElements] = this.elements
      this.tabContainerElement = tabContainerElement[0]
      this.tabElements = tabElements
      this.targetContainerElement = targetContainerElement[0]
      this.targetElements = targetElements
      this.callback = callback
      this.funcSettings = funcSettings
      this.init()
    }
  
    init() {
      this.initializeInitialTabIndexState()
      this.attachEventListeners()
    }
  
    destroy() {
      this.detachEventListeners()
    }
  
    attachEventListeners() {
      this.tabElements.forEach((tabElement) => {
        tabElement.addEventListener('click', this.tabClickHandler)
        tabElement.addEventListener('keydown', this.arrowKeyHandler)
      })
    }
  
    detachEventListeners() {
      this.tabElements.forEach((tabElement) => {
        tabElement.removeEventListener('click', this.tabClickHandler)
        tabElement.removeEventListener('keydown', this.arrowKeyHandler)
      })
    }
  
    initializeInitialTabIndexState() {
      if (!this.tabElements) return
      this.tabElements.forEach((tabElement, index) => {
        if (index === 0) {
          tabElement.classList.add(this.classes.activeClass)
          tabElement.setAttribute('tabindex', 0)
        } else {
          tabElement.classList.remove(this.classes.activeClass)
          tabElement.setAttribute('tabindex', -1)
        }
      })
    }
  
    elementErrorHandle() {
      this.elements.forEach((element, index) => {
        if (!element) throw new Error(`${Object.values(this.selectors)[index]} not found`)
      })
    }
  
    classesErrorHandle() {
      Object.values(this.classes).forEach((className) => {
        if (!className) {
          throw new Error('Class name not found')
        }
      })
    }
  
    tabClickHandler = (event) => {
      activateMathcingTabAndContent(this.tabElements.indexOf(event.target), this.tabElements, this.targetElements, this.classes.activeClass, this.classes.targetActiveClass, this.callback, this.funcSettings)
    }
  
    arrowKeyHandler = (event) => {
      enableKeyBoardNavigation(event, event.target, this.tabElements.indexOf(event.target), this.tabElements)
    }
  }
  
  export default Tabs
  